import { Link } from 'react-router-dom';
import { articles, categories, type Article } from '@/mocks/archive';
import { formatViews, tone, type ToneRole } from '@/utils/chigua';

export function getCategory(slug: string) {
  return categories.find((c) => c.slug === slug);
}

export function getArticle(slug: string) {
  return articles.find((a) => a.slug === slug);
}

export function byCategory(slug: string) {
  return articles.filter((a) => a.category === slug);
}

export function CategoryBadge({ slug, small = false }: { slug: string; small?: boolean }) {
  const cat = getCategory(slug);
  if (!cat) return null;
  const t = tone[cat.role];
  return (
    <Link
      to={`/column/${slug}`}
      className={`inline-flex items-center gap-1 rounded-full ${t.chip} font-medium transition-opacity hover:opacity-90 ${
        small ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs'
      }`}
    >
      <i className={cat.icon} aria-hidden="true" />
      {cat.name}
    </Link>
  );
}

export function MetaLine({ a, light = false }: { a: Article; light?: boolean }) {
  return (
    <div className={`flex items-center gap-3 text-xs ${light ? 'text-background-200' : 'text-foreground-500'}`}>
      <span className="inline-flex items-center gap-1">
        <i className="ri-calendar-line" aria-hidden="true" /> {a.date}
      </span>
      <span className="inline-flex items-center gap-1">
        <i className="ri-eye-line" aria-hidden="true" /> {formatViews(a.views)}
      </span>
      <span className="inline-flex items-center gap-1">
        <i className="ri-time-line" aria-hidden="true" /> {a.readMin} 分钟
      </span>
    </div>
  );
}

export function SectionHead({
  kicker,
  title,
  desc,
  moreTo,
  moreText = '查看全部',
}: {
  kicker?: string;
  title: string;
  desc?: string;
  moreTo?: string;
  moreText?: string;
}) {
  return (
    <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
      <div>
        {kicker && (
          <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-primary-500">
            <span className="h-px w-6 bg-primary-400" aria-hidden="true" />
            {kicker}
          </p>
        )}
        <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">{title}</h2>
        {desc && <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">{desc}</p>}
      </div>
      {moreTo && (
        <Link
          to={moreTo}
          className="group inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-background-100 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-200"
        >
          {moreText}
          <i className="ri-arrow-right-line text-base transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
        </Link>
      )}
    </div>
  );
}

export function Breadcrumb({ items }: { items: { name: string; to?: string }[] }) {
  return (
    <nav aria-label="面包屑导航" className="mb-5 flex flex-wrap items-center gap-1 text-sm text-foreground-500">
      {items.map((it, i) => (
        <span key={i} className="inline-flex items-center gap-1">
          {i > 0 && <i className="ri-arrow-right-s-line text-foreground-300" aria-hidden="true" />}
          {it.to ? (
            <Link to={it.to} className="transition-colors hover:text-primary-600">
              {it.name}
            </Link>
          ) : (
            <span className="text-foreground-900">{it.name}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

/** 大图信息卡片（首页焦点） */
export function PostCardVertical({ a }: { a: Article }) {
  return (
    <article className="group overflow-hidden rounded-2xl bg-background-100 transition-colors">
      <Link to={`/news/${a.slug}`} className="relative block aspect-[3/2] overflow-hidden">
        <img
          src={a.hero}
          alt={a.title}
          title={a.title}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3">
          <CategoryBadge slug={a.category} small />
        </span>
      </Link>
      <div className="p-4 md:p-5">
        <h3 className="text-base font-bold leading-snug text-foreground-950 md:text-lg">
          <Link to={`/news/${a.slug}`} className="line-clamp-2 transition-colors hover:text-primary-600">
            {a.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{a.summary}</p>
        <div className="mt-4 border-t border-background-200 pt-3">
          <MetaLine a={a} />
        </div>
      </div>
    </article>
  );
}

/** 焦点大图（细长文本卡）用于 hero / 双栏 */
export function PostCardSpot({ a, light = false }: { a: Article; light?: boolean }) {
  return (
    <Link
      to={`/news/${a.slug}`}
      className="group flex gap-4 rounded-2xl p-3 transition-colors hover:bg-background-100/70"
    >
      <span className={`relative block h-24 w-32 shrink-0 overflow-hidden rounded-xl md:h-28 md:w-40 ${light ? '' : ''}`}>
        <img src={a.hero} alt={a.title} title={a.title} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
      </span>
      <span className="flex min-w-0 flex-col justify-center">
        <span className="mb-1 inline-flex w-fit"><CategoryBadge slug={a.category} small /></span>
        <span className="line-clamp-2 text-sm font-semibold leading-snug text-foreground-950 group-hover:text-primary-600">
          {a.title}
        </span>
        <span className="mt-1.5 text-xs text-foreground-500">
          {a.date} · {formatViews(a.views)} 阅读
        </span>
      </span>
    </Link>
  );
}

export function RankRow({ a, rank }: { a: Article; rank: number }) {
  const cat = getCategory(a.category);
  const t = cat ? tone[cat.role as ToneRole] : null;
  return (
    <Link
      to={`/news/${a.slug}`}
      className="group flex items-start gap-4 border-b border-background-200 py-4 transition-colors hover:bg-background-100/60"
    >
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-base font-black ${
          rank <= 3 ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-600'
        }`}
      >
        {rank}
      </span>
      <div className="min-w-0 flex-1">
        <h4 className="text-sm font-semibold leading-snug text-foreground-950 group-hover:text-primary-600 md:text-base">
          {a.title}
        </h4>
        <p className="mt-1.5 flex items-center gap-2 text-xs text-foreground-500">
          <span className={`inline-flex items-center gap-1 ${t ? t.text : ''}`}>
            <i className={cat?.icon} aria-hidden="true" /> {cat?.name}
          </span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-eye-line" aria-hidden="true" /> {formatViews(a.views)}
          </span>
        </p>
      </div>
    </Link>
  );
}